import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, tap, switchMap } from 'rxjs/operators';

interface OAuthResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private config: any;
  private tokenKey = 'access_token';
  private refreshTokenKey = 'refresh_token';
  private expiresKey = 'token_expires_in';
  private stateKey = 'oauth_state';
  private refreshingToken = false;

  constructor(
    private http: HttpClient,
    @Inject('ENVIRONMENT') private environment: any
  ) {
    this.config = this.environment.auth;
  }

  login(): void {
    const state = this.generateRandomState();
    localStorage.setItem(this.stateKey, state);

    const params = new URLSearchParams({
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      response_type: 'code',
      scope: this.config.scope,
      state: state
    });

    window.location.href = `${this.config.oauthServerUrl}/oauth/authorize?${params.toString()}`;
  }

  handleCallback(code: string, state: string): Observable<any> {
    const savedState = localStorage.getItem(this.stateKey);

    // Clear the saved state immediately to prevent reuse
    localStorage.removeItem(this.stateKey);

    if (!savedState || state !== savedState) {
      return throwError(() => new Error('Invalid state parameter'));
    }

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: this.config.clientId,
      client_secret: this.config.clientSecret,
      redirect_uri: this.config.redirectUri,
      code: code,
    });

    return this.http.post<any>(`${this.config.oauthServerUrl}/oauth/token`, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }).pipe(
      tap(response => {
        this.setSession(response);
      }),
      catchError(error => {
        // Clear any partial authentication data
        this.logout();
        console.error('Auth error:', error);
        return throwError(() => error);
      })
    );
  }

  private setSession(authResult: any): void {
    if (!authResult.access_token) {
      throw new Error('Invalid token response');
    }

    localStorage.setItem(this.tokenKey, authResult.access_token);
    if (authResult.refresh_token) {
      localStorage.setItem(this.refreshTokenKey, authResult.refresh_token);
    }
    if (authResult.expires_in) {
      localStorage.setItem(this.expiresKey, (Date.now() + (authResult.expires_in * 1000)).toString());
    }
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.expiresKey);
    localStorage.removeItem(this.stateKey);
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem(this.tokenKey);
    const expires = localStorage.getItem(this.expiresKey);

    if (!token) {
      return false;
    }

    // If we have a token but no expiration, consider it valid
    if (!expires) {
      return true;
    }

    // Check expiration if we have both
    return Date.now() < parseInt(expires, 10);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  private generateRandomState(): string {
    const array = new Uint32Array(8);
    window.crypto.getRandomValues(array);
    return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
  }

  refreshToken(): Observable<any> {
    const refreshToken = localStorage.getItem(this.refreshTokenKey);
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.config.clientId,
      client_secret: this.config.clientSecret,
      refresh_token: refreshToken,
    });

    return this.http.post<OAuthResponse>(`${this.config.oauthServerUrl}/oauth/token`, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }).pipe(
      tap(response => this.setSession(response)),
      catchError(error => {
        this.logout();
        window.location.href = '/login';
        return throwError(() => error);
      })
    );
  }

  handleUnauthorizedError(error: any): Observable<any> {
    if (error.status === 401 && !this.refreshingToken) {
      this.refreshingToken = true;
      return this.refreshToken().pipe(
        switchMap(() => {
          this.refreshingToken = false;
          return of(true);
        }),
        catchError(refreshError => {
          this.refreshingToken = false;
          this.logout();
          window.location.href = '/login';
          return throwError(() => refreshError);
        })
      );
    }
    return throwError(() => error);
  }
}
